import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import type { WorkoutLap } from '../types/database.tables'
import type { TablesInsert, TablesUpdate } from '../types/database.types'
import type { WorkoutWithData } from '../types/workout'
import { createDateRangeForQuery } from '../utils/dateUtils'

// Activity types are now stored directly as strings in the database
// No mapping needed - discipline equals activity_type

export interface UseScheduledWorkoutsReturn {
  loadWorkoutLaps: (workoutId: string) => Promise<WorkoutLap[]>
  createWorkout: (workout: WorkoutCreate) => Promise<WorkoutWithData>
  updateWorkout: (workoutId: string, workout: WorkoutUpdate) => Promise<void>
  deleteWorkout: (workoutId: string) => Promise<void>
  isLoading: boolean
}

export type WorkoutCreate = TablesInsert<'workouts'>
export type WorkoutUpdate = TablesUpdate<'workouts'>

// Function to load a single workout by ID
export const useGetWorkoutById = (workoutId: string | undefined) => {
  return useQuery<WorkoutWithData>({
    queryKey: ['workouts_with_session', workoutId],
    enabled: !!workoutId,
    queryFn: async () => {
      console.log('useGetWorkoutById:', workoutId)
      const { data, error } = await supabase
        .from('workouts')
        .select(`
          *,
          workout_sessions!workout_id (
            id,
            total_timer_time,
            avg_stroke_distance
          )
        `)
        .eq('id', workoutId!)
        .single()
      if (error) {
        throw error
      }

      // Add calculated fields including executed total duration from workout_sessions
      const discipline = data.activity_type || 'unknown'

      // Calculate executed total duration from joined workout_sessions
      let executedTotalDurationSeconds: number | null = 0
      if (data.workout_sessions && Array.isArray(data.workout_sessions)) {
        // Sum all duration_seconds from completed workout_session entries
        executedTotalDurationSeconds = (data.workout_sessions as { total_timer_time: number }[])
          .filter((wd) => wd.total_timer_time)
          .reduce((total, wd) => total + wd.total_timer_time, 0)
      }
      if (executedTotalDurationSeconds === 0) {
        executedTotalDurationSeconds = null
      }

      return {
        ...data,
        discipline,
        executedTotalDurationSeconds,
      } as WorkoutWithData
    },
  })
}

export const useWorkoutSteps = (workoutId: string) => {
  return useQuery({
    queryKey: ['workout_steps', workoutId],
    enabled: workoutId !== undefined,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('workout_steps')
        .select('*')
        .eq('workout_id', workoutId)
        .order('step_index', { ascending: true })
      if (error) {
        throw error
      }
      return data
    },
  })
}

export const useWorkoutsByDate = (effectiveUserId?: string, startDate?: Date, endDate?: Date) => {
  return useQuery<WorkoutWithData[]>({
    queryKey: ['workouts_with_sessions', { effectiveUserId, startDate, endDate }],
    enabled: !!effectiveUserId && !!startDate && !!endDate,
    queryFn: async () => {
      console.log('useWorkoutsByDate:', startDate)
      const dateRange = createDateRangeForQuery(startDate!, endDate!)
      const { data, error: dbError } = await supabase
        .from('workouts')
        .select(`
            *,
            workout_sessions(
              id,
              total_timer_time,
              device_model
            )
          `)
        .eq('user_id', effectiveUserId!)
        .gte('scheduled_date', dateRange.startDate)
        .lt('scheduled_date', dateRange.endDate)
        .order('scheduled_date', { ascending: true })
        .order('order', { ascending: true })
      if (dbError) {
        throw dbError
      }

      // Add calculated fields including executed total duration from workout_sessions
      const workoutsWithCalculatedFields = (data || []).map((workout) => {
        const discipline = workout.activity_type || 'unknown'

        // Calculate executed total duration from joined workout_sessions
        let executedTotalDurationSeconds: number | null = 0
        if (workout.workout_sessions && workout.workout_sessions.length > 0) {
          executedTotalDurationSeconds = workout.workout_sessions[0].total_timer_time
        }
        return {
          ...workout,
          discipline,
          executedTotalDurationSeconds,
          // Only set TSS if it's not already present (preserve existing TSS values)
          // tss: workout.tss ?? estimatedTSS
        } as WorkoutWithData
      })
      return workoutsWithCalculatedFields
    },
  })
}

/**
 * Hook to manage workouts for a specific user
 * @param targetUserId - Optional user ID to fetch workouts for (defaults to authenticated user)
 */
export const useWorkouts = (targetUserId?: string): UseScheduledWorkoutsReturn => {
  const { user } = useAuth()
  const effectiveUserId = targetUserId || user?.id
  const queryClient = useQueryClient()

  const loadWorkoutLaps = useCallback(
    async (workoutId: string): Promise<WorkoutLap[]> => {
      if (!effectiveUserId) {
        throw new Error('User not authenticated')
      }

      const queryKey = ['workout-laps', effectiveUserId, workoutId]

      try {
        return await queryClient.fetchQuery({
          queryKey,
          queryFn: async () => {
            const { data: workoutSessions, error: sessionError } = await supabase
              .from('workout_sessions')
              .select('id')
              .eq('workout_id', workoutId)
              .eq('user_id', effectiveUserId)

            if (sessionError) {
              throw sessionError
            }

            if (!workoutSessions || workoutSessions.length === 0) {
              return []
            }

            const sessionIds = workoutSessions.map((session) => session.id)

            const { data: laps, error: lapsError } = await supabase
              .from('workout_laps')
              .select('*')
              .in('workout_session_id', sessionIds)
              .eq('user_id', effectiveUserId)
              .order('lap_number', { ascending: true })

            if (lapsError) {
              throw lapsError
            }

            return laps || []
          },
        })
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load workout laps'
        console.error('Failed to load workout laps:', err)
        throw new Error(errorMessage)
      }
    },
    [effectiveUserId, queryClient]
  )

  const createWorkoutMutation = useMutation({
    mutationFn: async (workoutData: WorkoutCreate): Promise<WorkoutWithData> => {
      if (!effectiveUserId) {
        throw new Error('User not authenticated')
      }

      const cleanedWorkout = { ...workoutData }
      cleanedWorkout.user_id = effectiveUserId
      delete (cleanedWorkout as any).discipline
      delete (cleanedWorkout as any).intensity
      delete (cleanedWorkout as any).template_id
      delete (cleanedWorkout as any).intervals
      delete (cleanedWorkout as any).tss
      delete (cleanedWorkout as any).time

      const { data, error: dbError } = await supabase.from('workouts').insert(cleanedWorkout).select().single()
      if (dbError) {
        throw dbError
      }

      const discipline = cleanedWorkout.activity_type || 'unknown'
      return {
        ...data,
        discipline,
      } as WorkoutWithData
    },
    onSuccess: () => {
      // Invalidate the workouts list query to refresh calendar views
      queryClient.invalidateQueries({ queryKey: ['workouts_with_sessions'] })
    },
  })

  // Function to delete a workout from database
  const deleteWorkoutMutation = useMutation({
    mutationFn: async (workoutId: string) => {
      if (!effectiveUserId) {
        throw new Error('User not authenticated')
      }

      const { error: dbError, count } = await supabase.from('workouts').delete({ count: 'exact' }).eq('id', workoutId)

      if (dbError) {
        throw dbError
      }

      if (count === 0) {
        throw new Error('Workout could not be deleted - it may not exist or you may not have permission')
      }

      return workoutId
    },
    onSuccess: (workoutId) => {
      queryClient.removeQueries({ queryKey: ['workout-laps', effectiveUserId, workoutId] })
      queryClient.removeQueries({ queryKey: ['workouts_with_session', workoutId] })
      // Invalidate the workouts list query to refresh calendar views
      queryClient.invalidateQueries({ queryKey: ['workouts_with_sessions'] })
    },
  })

  // Function to update an existing workout in database
  const updateWorkoutMutation = useMutation({
    mutationFn: async ({
      workoutId,
      workoutData,
    }: {
      workoutId: string
      workoutData: WorkoutUpdate
    }): Promise<WorkoutWithData> => {
      if (!effectiveUserId) {
        throw new Error('User not authenticated')
      }

      const cleaned = { ...workoutData }
      cleaned.user_id = effectiveUserId
      delete (cleaned as any).discipline
      delete (cleaned as any).intensity
      delete (cleaned as any).template_id
      delete (cleaned as any).intervals
      delete (cleaned as any).tss
      delete (cleaned as any).workout_sessions

      const { data, error: dbError } = await supabase
        .from('workouts')
        .update(cleaned)
        .eq('id', workoutId)
        .select()
        .single()

      if (dbError) {
        throw dbError
      }

      const discipline = data.activity_type || 'unknown'
      return {
        ...data,
        discipline,
      } as WorkoutWithData
    },
    onSuccess: (updatedWorkout) => {
      queryClient.setQueryData(['workouts_with_session', updatedWorkout.id], updatedWorkout)
      // Invalidate the workouts list query to refresh calendar views
      queryClient.invalidateQueries({ queryKey: ['workouts_with_sessions'] })
    },
  })

  const createWorkout = useCallback(
    async (workoutData: WorkoutCreate): Promise<WorkoutWithData> => {
      return createWorkoutMutation.mutateAsync(workoutData)
    },
    [createWorkoutMutation]
  )

  const deleteWorkout = useCallback(
    async (workoutId: string): Promise<void> => {
      await deleteWorkoutMutation.mutateAsync(workoutId)
    },
    [deleteWorkoutMutation]
  )

  const updateWorkout = useCallback(
    async (workoutId: string, workoutData: WorkoutUpdate): Promise<void> => {
      await updateWorkoutMutation.mutateAsync({ workoutId, workoutData })
    },
    [updateWorkoutMutation]
  )

  const isLoading =
    createWorkoutMutation.isPending || deleteWorkoutMutation.isPending || updateWorkoutMutation.isPending

  return {
    loadWorkoutLaps,
    createWorkout,
    updateWorkout,
    deleteWorkout,
    isLoading,
  }
}
